import React, {
  createContext,
  ReactNode,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { DEFAULT_BACKGROUND_IMAGE } from "../common/backgrounds";

/**
 * Стили глобального фона и его позиционирования.
 * `backgroundImage` — путь к PNG в `public/` (например `/images/backgrounds/home-base.png`).
 */
export interface IStyles {
  backgroundImage?: string;
  bottom?: string;
  opacity?: string;
  /** Высота нижней панели select-tariff — для центрирования заголовка над ней. */
  sheetBottomInset?: string;
}

export interface IStylesContextValue {
  styles: IStyles;
  setStyles: (styles: Partial<IStyles>) => void;
  resetStyles: () => void;
}

export const StylesContext = createContext<IStylesContextValue | null>(null);

const DEFAULT_STYLES: IStyles = {
  backgroundImage: DEFAULT_BACKGROUND_IMAGE,
  bottom: "0",
  opacity: "1",
};

interface StylesProviderProps {
  children: ReactNode;
}

export const StylesProvider = ({ children }: StylesProviderProps) => {
  const [styles, setStylesState] = useState<IStyles>(DEFAULT_STYLES);

  const setStyles = useCallback((patch: Partial<IStyles>) => {
    setStylesState((prev) => ({ ...prev, ...patch }));
  }, []);

  const resetStyles = useCallback(() => {
    setStylesState(DEFAULT_STYLES);
  }, []);

  const value = useMemo<IStylesContextValue>(
    () => ({
      styles,
      setStyles,
      resetStyles,
    }),
    [styles, setStyles, resetStyles]
  );

  return <StylesContext.Provider value={value}>{children}</StylesContext.Provider>;
};

export const useStyles = () => {
  const context = useContext(StylesContext);
  if (!context) {
    throw new Error("useStyles must be used within a StylesProvider");
  }
  return context;
};
